import { useState, type FormEvent } from 'react'
import { Box, TextField, Button, Typography, Alert, Link as MuiLink } from '@mui/material'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { DASHBOARD_AUTH_PATHS } from '@yourownmetric/shared'

import { needsMfa, useLoginMutation, useRegisterMutation } from '@/apis/authApi'
import { useAppDispatch } from '@/app/hooks'

import { setCredentials } from './authSlice'
import { GoogleSignInButton } from './GoogleSignInButton'
import { safeNextPath } from './nextPath'

export function RegisterPage() {
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [tenantName, setTenantName] = useState('')
    const [register, { isLoading, error }] = useRegisterMutation()
    const [login, { isLoading: signingIn }] = useLoginMutation()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    // `?next=` (e.g. back to /app/connect…): same open-redirect guard as sign-in.
    const [params] = useSearchParams()
    const next = safeNextPath(params.get('next'))
    const loginPath = params.get('next')
        ? `${DASHBOARD_AUTH_PATHS.login}?next=${encodeURIComponent(next)}`
        : DASHBOARD_AUTH_PATHS.login

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        try {
            await register({ name, email, password, tenantName }).unwrap()
        } catch {
            return // error handled by RTK Query
        }
        // /auth/register returns the new ids, not a session: sign in with the same credentials.
        try {
            const session = await login({ email, password }).unwrap()
            if (needsMfa(session)) {
                navigate(loginPath)
                return
            }
            dispatch(setCredentials(session))
            navigate(next)
        } catch {
            navigate(loginPath)
        }
    }

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400, mx: 'auto' }}
        >
            <Typography variant="h4" textAlign="center">
                Create Account
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
                Start your free analytics journey
            </Typography>

            {error && <Alert severity="error">Registration failed. Please try again.</Alert>}

            <TextField
                label="Your Name"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                fullWidth
            />
            <TextField
                label="Organization Name"
                value={tenantName}
                onChange={e => setTenantName(e.target.value)}
                required
                fullWidth
                helperText="This will be your workspace name"
            />
            <TextField
                label="Email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                fullWidth
            />
            <TextField
                label="Password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                fullWidth
                inputProps={{ minLength: 8 }}
                helperText="Minimum 8 characters"
            />

            <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading || signingIn}
                fullWidth
            >
                {isLoading || signingIn ? 'Creating account…' : 'Create Account'}
            </Button>

            <GoogleSignInButton text="signup_with" next={next} />

            <Typography variant="body2" textAlign="center">
                Already have an account?{' '}
                <MuiLink component={Link} to={loginPath}>
                    Sign in
                </MuiLink>
            </Typography>
        </Box>
    )
}
